import { Text } from "@/components/ui/Typography";
import { cn } from "@/lib/utils";
import { Check } from "lucide-react";
import { Fragment } from "react";

export type StepState = "active" | "completed" | "not-started";

export interface StepperStep {
  label: string;
  state: StepState;
}

interface StepperProps {
  steps: StepperStep[];
  className?: string;
  showLabels?: boolean;
  lineClassName?: string;
  stepClassName?: string;
  labelClassName?: string;
}

function StepLine({ className }: { className?: string }) {
  return (
    <div className={cn("w-2.5 md:w-10 lg:w-20 h-0.5 bg-gray-6", className)} />
  );
}

function StepCircle({ state }: { state: StepState }) {
  if (state === "active") {
    return (
      <div className="size-5 flex-shrink-0 border-primary border-2 rounded-full p-0.5 flex items-center justify-center">
        <div className="aspect-square flex-1 bg-primary rounded-full" />
      </div>
    );
  }

  return (
    <div
      className={cn(
        "size-5 flex-shrink-0 bg-primary opacity-30 rounded-full flex items-center justify-center",
        state === "completed" && "opacity-100",
      )}
    >
      {state === "completed" && (
        <Check className="size-3 text-primary-foreground" strokeWidth={4} />
      )}
    </div>
  );
}

function StepItem({
  state,
  label,
  showLabel = true,
  className,
  labelClassName,
}: {
  state: StepState;
  label: string;
  showLabel?: boolean;
  className?: string;
  labelClassName?: string;
}) {
  return (
    <div className={cn("flex items-center gap-2.5", className)}>
      <StepCircle state={state} />
      {showLabel && (
        <Text
          variant="caption"
          weight={state === "active" ? "semibold" : "medium"}
          className={cn(
            "sr-only lg:not-sr-only lg:w-max",
            state === "not-started" && "opacity-40",
            labelClassName,
          )}
        >
          {label}
        </Text>
      )}
    </div>
  );
}

export function MultiStepFormStepper({
  steps,
  className,
  showLabels = true,
  lineClassName,
  stepClassName,
  labelClassName,
}: StepperProps) {
  return (
    <div
      className={cn(
        "flex flex-row w-full gap-2.5 items-center justify-center",
        className,
      )}
    >
      {steps.map((step, index) => (
        <Fragment key={`step-${index}`}>
          <StepItem
            state={step.state}
            label={step.label}
            showLabel={showLabels}
            className={stepClassName}
            labelClassName={labelClassName}
          />
          {index < steps.length - 1 && <StepLine className={lineClassName} />}
        </Fragment>
      ))}
    </div>
  );
}
